import { useTheme } from "@/components/theme/themeProvider"
import { Button } from "@/components/ui/baseUi/button"
import { UserCellVariants } from "@/components/ui/layout/table/userCell"
import { Text } from "@/components/ui/baseUi/text"
import { useCurrentBox } from "@/modules/box/hooks/useBox"
import { BoxStaffPermissions } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@/modules/boxStaff/hooks/useBoxStaffPermissions"
import { FeatureKeys } from "@/modules/featureToggle/featureToggleTypes"
import { useFeatureToggle } from "@/modules/featureToggle/hooks/useFeatureToggle"
import { PayoutEnumApi } from "@/modules/finances/financesPayout/gateway/payoutApi"
import { TransactionOutOfBandPayment } from "@/modules/finances/transactions/components/transactionOutOfBandPayment"
import { createInvoicePaymentDetailsModel } from "@/modules/finances/transactions/models/invoicePaymentDetailsModel"
import { cn } from "@/modules/lib/utils"
import { Path } from "@/modules/navigation/routes"
import { word } from "@modules/core/utils/i18n"
import { TransactionDetailsEntity } from "@modules/finances/entities/transactionDetailsEntity"
import { useFinanceSidebar } from "@modules/finances/hooks/useFinanceSidebar"
import {
  getTransactionErrorReasonWording,
  getTransactionHistoryColorStatus,
  getTransactionHistoryStatusWording,
} from "@modules/finances/transactions/utils/transactionUtils"
import { ApiFinanceType } from "@modules/finances/types/financesType"
import dayjs from "dayjs"
import { ChevronRightIcon } from "lucide-react"
import { useNavigate } from "react-router"

type InvoicePaymentDetailsProps = {
  transactionDetails: TransactionDetailsEntity
  apiFinanceType: ApiFinanceType
  className?: string
} & React.ComponentProps<"div">

export function InvoicePaymentDetails({
  transactionDetails,
  apiFinanceType,
  className,
  ...props
}: InvoicePaymentDetailsProps) {
  const { theme } = useTheme()
  const { navigateToPayoutDetails } = useFinanceSidebar()
  const navigate = useNavigate()
  const isPaymentAtCounterToggleActive = useFeatureToggle(FeatureKeys.PayOutOfBand)
  const { result: currentBox } = useCurrentBox()
  const canEditFinancial = useBoxStaffPermission(BoxStaffPermissions.EDIT_FINANCIAL) || !currentBox

  const {
    colorStatus,
    wordingStatus,
    isOutOfBandPaymentEnabled,
    reasonWording,
    errorActionWording,
    errorAction,
    shouldDisplayPaymentMethod,
    shouldDisplayHistory,
    shouldDisplayOutOfBandPayment,
    shouldDisplayReason,
    paymentMethodLabel,
    paymentMethodDetails,
    outOfBandPaymentMethodLabel,
    shouldDisplayPayoutDate,
  } = createInvoicePaymentDetailsModel(transactionDetails, currentBox, isPaymentAtCounterToggleActive, theme)

  const handleContactSupport = () => {
    window.open(word("global.support.zendesk"), "_blank")
  }
  const handleContactAthlete = () => {
    if (transactionDetails.user?.id) {
      if (currentBox) {
        navigate(Path.Chat.Root, {
          state: {
            userToChatTo: transactionDetails.user.id,
            returnTo: {
              target: "box-payin",
              transactionId: transactionDetails.transactionId,
            },
          },
        })
      } else {
        navigate(Path.Chat.Root, {
          state: {
            userToChatTo: transactionDetails.user.id,
          },
        })
      }
    }
  }

  const handleNavigateToPayoutDetails = () => {
    if (transactionDetails.payout?.payoutId) {
      navigateToPayoutDetails(
        apiFinanceType === ApiFinanceType.BOX ? PayoutEnumApi.OWNER : PayoutEnumApi.ATHLETE,
        transactionDetails.payout.payoutId,
        {
          replaceAllComponents: false,
          onHide: () => {
            return Promise.resolve(true)
          },
        },
      )
    }
  }

  return (
    <div className={cn("flex flex-col gap-3 rounded-4xl bg-surface-foreground p-6", className)} {...props}>
      <Text fontStyle="BS" className="text-textDefault-body uppercase">
        {word("finances.details.payment")}
      </Text>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <Text fontStyle="BS" className="text-textOnSurface-muted">
            {word("finances.details.paymentStatus")}
          </Text>

          <div className="flex flex-row gap-2">
            <Text fontStyle="LL" className={UserCellVariants({ subtitleColor: colorStatus })}>
              {wordingStatus}
            </Text>
            <Text fontStyle="BM" className="text-textDefault-body">
              {dayjs(transactionDetails.date).format("LL")}
            </Text>
          </div>
        </div>

        {shouldDisplayPayoutDate && (
          <div className="flex flex-row items-center gap-2">
            <Text fontStyle="LM">{word("finances.details.paidOut.label")}</Text>
            <Text fontStyle="BM">{dayjs(transactionDetails.payout?.payoutAt).format("LL")}</Text>
            <Button variant="underline" size="md" onClick={handleNavigateToPayoutDetails}>
              {word("finances.details.paidOut.seePayout")}
              <ChevronRightIcon size={16} />
            </Button>
          </div>
        )}

        {shouldDisplayReason && (
          <div className="flex flex-col gap-3">
            <Text
              fontStyle="BM"
              className="text-textOnSurface-subtle"
            >{`${word("finances.details.failed.reason")}: ${reasonWording}`}</Text>
            <div className="flex flex-col">
              <Text fontStyle="BS" className="text-textOnSurface-muted">
                {word("finances.details.failed.action")}
              </Text>
              <Text fontStyle="BM" className="text-textOnSurface-subtle">
                {errorActionWording}
              </Text>

              {errorAction === "contactSupport" ? (
                <Button variant="secondary" size="md" className="mt-1 w-fit" onClick={handleContactSupport}>
                  {word("finances.details.failed.contactSupport")}
                </Button>
              ) : errorAction === "contactAthlete" && transactionDetails.user?.id ? (
                <Button variant="secondary" size="md" className="mt-1 w-fit" onClick={handleContactAthlete}>
                  {word("finances.details.failed.contactAthlete")}
                </Button>
              ) : null}
            </div>
          </div>
        )}

        {shouldDisplayHistory && (
          <div className="flex flex-col gap-1">
            <Text fontStyle="BS" className="text-textOnSurface-muted">
              {word("finances.details.history.title")}
            </Text>
            <div className="flex flex-col gap-1 border-l pl-2">
              {transactionDetails.history?.map((history, index) => (
                <div className="flex flex-row items-center gap-2" key={index}>
                  <Text fontStyle="BM">{dayjs(history.date).format("L")}</Text>
                  <Text fontStyle="LM" className={cn(getTransactionHistoryColorStatus(history.status))}>
                    {history.errorCode
                      ? `${getTransactionHistoryStatusWording(history.status)} - ${getTransactionErrorReasonWording(history.errorCode)}`
                      : getTransactionHistoryStatusWording(history.status)}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        )}

        {shouldDisplayOutOfBandPayment && (
          <div className="mt-3 flex flex-col gap-1">
            <Text fontStyle="BS" className="text-textOnSurface-muted">
              {word("finances.page.table.header.paymentMethod")}
            </Text>

            <div className="flex flex-row items-end gap-2">
              <Text fontStyle="TS" className="text-textDefault-heading">
                {outOfBandPaymentMethodLabel}
              </Text>
            </div>
          </div>
        )}
      </div>

      {isOutOfBandPaymentEnabled && canEditFinancial && (
        <TransactionOutOfBandPayment transactionDetails={transactionDetails} />
      )}

      {shouldDisplayPaymentMethod && (
        <div className="flex flex-col gap-1">
          <Text fontStyle="BS" className="text-textOnSurface-muted">
            {paymentMethodLabel}
          </Text>

          <Text fontStyle="TS" className="text-textDefault-heading">
            {paymentMethodDetails}
          </Text>
        </div>
      )}
    </div>
  )
}
